import { FC, ReactNode, useEffect, useState } from "react";

import CheckmarkIcon from "@src/lib/icons/18px/checkmark";
import TriangleWarningOutlineIcon from "@src/lib/icons/18px/triangle-warning-outline";
import Spinner from "@src/lib/ui/Spinner";
import Text from "@src/lib/ui/Text";

type Options = {
  queueLength: number;
  lastSuccessfulSyncAt: Date | null;
};

// Queue continuously non-empty for this long means saving is stuck:
// escalate the quiet "Saving" spinner to a warning the user cannot
// mistake for progress (OC-779: 72min of edits looked "Saving" while
// none ever reached the server).
export const WARN_AFTER_UNSAVED_SECONDS = 120;

/**
 * "Saving" spinner while items are queued for the builder-actions
 * flush; "Autosaved" checkmark once the queue drains successfully.
 * If the queue stays non-empty for WARN_AFTER_UNSAVED_SECONDS the
 * spinner escalates to a persistent "Not saved" warning until the
 * queue drains. Returns null (and stays null) until the first time
 * this mount sees a non-empty queue — a fresh page-load shouldn't
 * flash "Autosaved" just because the store holds a stale timestamp.
 * (Items stranded in the queue from before the mount count too:
 * they're still unsaved work, so showing "Saving" for them is honest.)
 *
 * Callers use `!!node` to decide whether to render the wrapping
 * status row (which may also host a publish pill / agent activity /
 * etc from the same domain).
 */
export const useBuilderSavingIndicator = ({
  queueLength,
  lastSuccessfulSyncAt,
}: Options): ReactNode | null => {
  const [showIndicator, setShowIndicator] = useState(false);
  const [unsavedSince, setUnsavedSince] = useState<Date | null>(null);
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    if (queueLength > 0) {
      setShowIndicator(true);
      setUnsavedSince((current) => current ?? new Date());
    } else {
      setUnsavedSince(null);
      setStuck(false);
    }
  }, [queueLength]);
  useEffect(() => {
    if (!unsavedSince) return;
    const remaining =
      WARN_AFTER_UNSAVED_SECONDS * 1000 - (Date.now() - unsavedSince.getTime());
    if (remaining <= 0) {
      setStuck(true);
      return;
    }
    const timer = setTimeout(() => setStuck(true), remaining);
    return () => clearTimeout(timer);
  }, [unsavedSince]);
  if (!showIndicator) return null;
  if (queueLength > 0 && stuck) return <NotSavedWarningLabel />;
  if (queueLength > 0) return <SavingLabel />;
  if (lastSuccessfulSyncAt) return <AutosavedLabel />;
  return null;
};

const SavingLabel: FC = () => (
  <div className="flex items-center gap-1 text-muted-foreground">
    <Spinner size={10} className="text-gray-7" style={{ marginLeft: 2 }} />
    <Text variant="fine-print-description">Saving</Text>
  </div>
);

const AutosavedLabel: FC = () => (
  <div className="flex items-center gap-1 text-muted-foreground">
    <CheckmarkIcon fontSize={12} width={12} height={12} />
    <Text variant="fine-print-description">Autosaved</Text>
  </div>
);

const NotSavedWarningLabel: FC = () => (
  <div
    role="alert"
    title="Your recent edits have not reached the server. Keep this tab open — closing or reloading it will discard them."
    className="flex items-center gap-1 text-status-error-text"
  >
    <TriangleWarningOutlineIcon width={12} height={12} />
    <Text variant="fine-print-description">Not saved — keep this tab open</Text>
  </div>
);
